/**
 * Типи для кастомних трансформерів запитів
 */

import type { Options } from 'ky'

/**
 * Функція-трансформер для модифікації опцій запиту
 * @param options - оригінальні опції запиту
 * @returns модифіковані опції
 */
export type RequestTransformer = (options: Options) => Options

/**
 * Вбудовані трансформери для популярних API форматів
 */
export const RequestTransformers = {
    /**
     * API Platform формат
     * Встановлює Content-Type: application/ld+json
     * Використовується для GET, POST, PUT запитів до API Platform
     */
    apiPlatform: (options: Options): Options => {
        const opts = { ...options }

        if (!opts.headers) {
            opts.headers = {}
        }

        const headers = opts.headers as Record<string, string>

        // Встановлюємо Content-Type тільки якщо його ще немає
        if (!headers['Content-Type']) {
            headers['Content-Type'] = 'application/ld+json'
        }

        // Встановлюємо Accept для отримання JSON-LD відповідей
        if (!headers['Accept']) {
            headers['Accept'] = 'application/ld+json'
        }

        return opts
    },

    /**
     * Laravel формат
     * Встановлює Content-Type: application/json
     * Стандартний JSON формат для Laravel API
     */
    laravel: (options: Options): Options => {
        const opts = { ...options }

        if (!opts.headers) {
            opts.headers = {}
        }

        const headers = opts.headers as Record<string, string>

        if (!headers['Content-Type']) {
            headers['Content-Type'] = 'application/json'
        }

        if (!headers['Accept']) {
            headers['Accept'] = 'application/json'
        }

        return opts
    },

    /**
     * Стандартний JSON формат
     * Встановлює Content-Type: application/json
     * Використовується для більшості REST API
     */
    json: (options: Options): Options => {
        const opts = { ...options }

        if (!opts.headers) {
            opts.headers = {}
        }

        const headers = opts.headers as Record<string, string>

        if (!headers['Content-Type']) {
            headers['Content-Type'] = 'application/json'
        }

        if (!headers['Accept']) {
            headers['Accept'] = 'application/json'
        }

        return opts
    },

    /**
     * Multipart form data
     * Використовується для завантаження файлів
     * НЕ встановлює Content-Type, бо браузер сам додає boundary
     */
    multipart: (options: Options): Options => {
        const opts = { ...options }

        if (!opts.headers) {
            opts.headers = {}
        }

        const headers = opts.headers as Record<string, string>

        // Видаляємо Content-Type якщо він є, щоб браузер сам встановив з boundary
        delete headers['Content-Type']

        return opts
    },

    /**
     * Form URL encoded
     * Використовується для традиційних форм
     */
    formUrlEncoded: (options: Options): Options => {
        const opts = { ...options }

        if (!opts.headers) {
            opts.headers = {}
        }

        const headers = opts.headers as Record<string, string>

        if (!headers['Content-Type']) {
            headers['Content-Type'] = 'application/x-www-form-urlencoded'
        }

        return opts
    },

    /**
     * Не змінювати заголовки
     */
    none: (options: Options): Options => options,
}

/**
 * Тип для назв вбудованих трансформерів запитів
 */
export type RequestTransformerName = keyof typeof RequestTransformers
